/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';
import * as sentryConfig from '../components/global/Sentry/sentryConfig';
import * as axiosUtils from './axios';
import { getConfig } from './configs';
import { toUpper, get, omitBy, omit, isNil, extend, pick, includes } from '@utils/lodash';

function handleAPIError(err: any, metadata: { [key: string]: any }, errorType: string) {
  const errorCode: number = get(err, 'response.status');
  const name = 'APIERROR';
  const url = get(err, 'config.url', 'unknown url');
  const extensions: { [key: string]: any } = {
    errorCode,
    url,

    // Replace all uuid's with ":id" so that we can easily look up number of errors for a given
    // endpoint in sentry by indexing this particular url
    endpoint: url.replace(/[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}/gi, ':id'),
    axiosCode: get(err, 'code', null),
    httpMethod: toUpper(get(err, 'config.method', 'unknown http method')),
  };

  // We don't want to put the url in the error message because it is unique per room
  // and may cause issues with error grouping. Instead, we want it to be stored as the same error for a given endpoint.
  const uniqueApiMetaProps: string[] = ['url'];
  const apiErrorMetadata = omitBy(omit(extensions, uniqueApiMetaProps), isNil);
  const msg = [`[${name}]`, JSON.stringify(apiErrorMetadata)].join('; ');

  // Add more metadata for error logging around api issues
  extend(metadata, apiErrorMetadata);

  // Sanitize error for PII
  // We are explicitly re-writing and removing properties from this error
  // rather than creating a new Error with a sanitized message to preserve
  // stack traces for sentry.
  console.warn('MUTATING ERROR');
  err.message = msg;
  delete err.code;
  delete err.response;
  delete err.config;
  delete err.request;

  Sentry.withScope(scope => {
    scope.setTag('errorType', errorType);
    sentryConfig.captureException(err, metadata);
  });
}

export type DiagnosticsErrorMetadata = sentryConfig.SentryMetadata;
export interface DiagnosticsOpts {
  ignoreError?: (exception: Error, metaData: DiagnosticsErrorMetadata) => boolean;
}

export interface DiagnosticsInterface {
  error: (exception: Error, metadata: DiagnosticsErrorMetadata) => any;
  notifyInvalidState: (stateDescription: string) => void;
  breadcrumb: (message: string, breadcrumbMetadata: {}) => void;
  warn: (message: string) => void;
  info: (message: string) => void;
}

export class Diagnostics implements DiagnosticsInterface {
  private opts: DiagnosticsOpts;

  constructor(opts: DiagnosticsOpts = {}) {
    this.opts = opts;
  }

  error(exception: Error, metadata: DiagnosticsErrorMetadata = {}): any {
    if (this.opts.ignoreError && this.opts.ignoreError(exception, metadata)) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error(`Diagnostics.error(): ${exception.message}`, exception.stack);

    extend(metadata, {
      locationOrigin: window.location.origin,
    });
    if (axiosUtils.isAxiosError(exception)) {
      const { nodeEnv } = getConfig();
      /* istanbul ignore next devonly */
      if (nodeEnv === 'dev') {
        console.warn('network error details', exception && pick((exception as any).config, ['method', 'url', 'data']));
      }

      return handleAPIError(exception, metadata, 'APIError');
    } else if (axiosUtils.isNetworkError(exception)) {
      return handleAPIError(exception, metadata, 'NetworkError');
    }

    // Sentry tried to group things very poorly, so we're going to do something simple and just group
    // issues by error message.
    if (!metadata.fingerprint) {
      metadata.fingerprint = [exception.message];
    }

    Sentry.withScope(scope => {
      scope.setTag('errorType', 'Error');
      sentryConfig.captureException(exception, metadata);
    });
  }

  notifyInvalidState(stateDescription: string) {
    console.warn('Forwarding invalid state: ', stateDescription);
    Sentry.captureMessage(`InvalidState: ${stateDescription}`);
  }

  breadcrumb(message: string, breadcrumbMetadata: {} = {}) {
    // We want to do console.info in lower-level envs because it's useful for debugging and development,
    // but it's noisy and duplicative in error reporting in higher-level envs
    const { nodeEnv } = getConfig();
    /* istanbul ignore next devonly */
    if (includes(['dev', 'sandbox'], nodeEnv)) {
      console.info(message, breadcrumbMetadata);
    }
    sentryConfig.addBreadcrumb(message, breadcrumbMetadata);
  }

  warn(message: string) {
    console.warn(message);
  }

  info(message: string) {
    console.info(message);
  }
}

export const diagnostics = new Diagnostics();
